/**
 * useApi — the thin hooks every feature uses for server state. Wraps TanStack Query so callers
 * never touch fetch directly and always get a normalized `ApiError`.
 *
 *   const dossiers = useApiQuery(qk.scoped(labId, "reception", "dossier"), "/reception/dossiers");
 *   const create   = useApiMutation((body) => api.post("/reception/dossiers", body), {
 *                      invalidate: [qk.scoped(labId, "reception", "dossier")] });
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, type Query } from "./client";
import type { ApiError } from "./types";

/** GET a path into a typed query. */
export function useApiQuery<TData>(
  queryKey: QueryKey,
  path: string,
  options?: {
    query?: Query;
    enabled?: boolean;
    select?: UseQueryOptions<TData, ApiError, TData>["select"];
    staleTime?: number;
    /** Poll on an interval (ms) — e.g. a live heartbeat-driven badge. Off by default. */
    refetchInterval?: number;
  },
) {
  return useQuery<TData, ApiError>({
    queryKey,
    queryFn: ({ signal }) => api.get<TData>(path, options?.query, signal),
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
    ...(options?.select ? { select: options.select } : {}),
    ...(options?.staleTime !== undefined ? { staleTime: options.staleTime } : {}),
    ...(options?.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval } : {}),
  });
}

/** A mutation that invalidates the given keys on success (CRUD-complete / supersede-by-reissue, L-1). */
export function useApiMutation<TData, TVars>(
  mutationFn: (vars: TVars) => Promise<TData>,
  options?: {
    invalidate?: ReadonlyArray<QueryKey>;
    onSuccess?: UseMutationOptions<TData, ApiError, TVars>["onSuccess"];
    onError?: UseMutationOptions<TData, ApiError, TVars>["onError"];
  },
) {
  const qc = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn,
    onSuccess: async (data, vars, ctx, mutation) => {
      if (options?.invalidate) {
        await Promise.all(
          options.invalidate.map((key) => qc.invalidateQueries({ queryKey: key })),
        );
      }
      await options?.onSuccess?.(data, vars, ctx, mutation);
    },
    ...(options?.onError ? { onError: options.onError } : {}),
  });
}

/** Re-export the raw verb client for imperative calls (loaders, command palette actions). */
export { api } from "./client";
